/**
 * ChannelRollupsApi — the Roll-ups page's creator links (CUR-1910,
 * `/admin/channel-intelligence/creator-links`) and auto-refresh opt-in (CUR-1911,
 * `/admin/channel-intelligence/rollups/{name}/auto-refresh`).
 *
 * Its own mixin module because channels-api.ts sits at its file-size ratchet
 * (`scripts/ci/check_file_size_ratchet.py`). Methods-only: prototype methods are
 * merged onto AdminAPIClient via applyMixins in client.ts. Responses keep their
 * snake_case keys, like the other roll-up methods.
 */

import type {
  CreatorLinkResponse,
  RollupAutoRefreshResponse,
  SaveCreatorLinksRequest,
  SaveCreatorLinksResponse,
} from '../../../../shared/types/api';

import { BaseApiClient } from './base';

const CREATOR_LINKS = '/admin/channel-intelligence/creator-links';

export class ChannelRollupsApi extends BaseApiClient {
  /** Every confirmed link touching these publishers. Super-admin. */
  async listCreatorLinks(publisherIds: number[]): Promise<CreatorLinkResponse[]> {
    const query = publisherIds.map((id) => `publisher_ids=${encodeURIComponent(id)}`).join('&');
    return this.fetchJSON<CreatorLinkResponse[]>(`${CREATOR_LINKS}?${query}`, { transformKeys: false });
  }

  /** Create or confirm a creator from a set of publishers. Idempotent. Super-admin. */
  async saveCreatorLinks(request: SaveCreatorLinksRequest): Promise<SaveCreatorLinksResponse> {
    return this.fetchJSON<SaveCreatorLinksResponse>(CREATOR_LINKS, { method: 'POST', body: request, transformKeys: false });
  }

  /** Delete one link; the creator row is kept. Super-admin. */
  async deleteCreatorLink(creatorId: string, publisherId: number): Promise<{ deleted: boolean }> {
    return this.fetchJSON<{ deleted: boolean }>(
      `${CREATOR_LINKS}/${encodeURIComponent(creatorId)}/${encodeURIComponent(publisherId)}`,
      { method: 'DELETE', transformKeys: false },
    );
  }

  /** Opt a roll-up in or out of the daily auto-refresh. 409 when no confirmed links back it. Super-admin. */
  async setRollupAutoRefresh(normalizedName: string, organizationId: string, enabled: boolean): Promise<RollupAutoRefreshResponse> {
    return this.fetchJSON<RollupAutoRefreshResponse>(
      `/admin/channel-intelligence/rollups/${encodeURIComponent(normalizedName)}/auto-refresh`,
      { method: 'PUT', body: { organization_id: organizationId, enabled }, transformKeys: false },
    );
  }
}
